/**
 * Billable work-order items (INVOICE-BY-ITEM.md §5).
 * GET  /api/invoices/billable-work-orders  — the customer's completed / partly invoiced work orders, free lines only
 * POST /api/invoices/from-work-order-items — draft invoice from chosen lines
 * POST /api/invoices/{id}/work-order-items — add chosen lines to a draft
 */

import ApiService from '../api.service';

const PAGE_SIZE = 20;

const toItem = (li) => ({
  id: li.id,
  serviceName: li.service_name || 'Service',
  customerName: li.customer_name || '',
  lineTotal: Number(li.line_total || 0),
  ready: true, // only lines of completed work orders are listed
  invoice: null, // only free lines are listed
});

const toWorkOrder = (wo) => ({
  id: wo.work_order_id,
  number: wo.work_order_number,
  createdAt: wo.created_at,
  completedAt: wo.completed_at,
  status: wo.status,
  invoicedItems: Number(wo.invoiced_items || 0),
  totalItems: Number(wo.total_items || 0),
  items: (wo.items || []).map(toItem),
});

export const billableItemsService = {
  // One page of the customer's work orders with lines to bill (organizationId, or walkIn for walk-in customers).
  listWorkOrders: async ({ typingCenterId, organizationId, walkIn, invoiceId, search, page = 0 }) => {
    const res = await ApiService.get('/api/invoices/billable-work-orders', {
      params: {
        typing_center_id: typingCenterId || undefined,
        internal_organization_id: walkIn ? undefined : organizationId,
        walk_in: walkIn ? true : undefined,
        invoice_id: invoiceId || undefined,
        search: search || undefined,
        page,
        size: PAGE_SIZE,
      },
    });
    const data = res?.data?.data || {};
    const content = data.content || [];
    return {
      workOrders: content.map(toWorkOrder),
      hasMore: (data.page ?? page) + 1 < (data.total_pages || 0),
    };
  },

  // Draft invoice from the chosen lines; mappedOrg = { id, name, code } (Bill to).
  createFromItems: async ({ typingCenterId, organizationId, walkIn, mappedOrg, workOrderLineItemIds }) => {
    const res = await ApiService.post('/api/invoices/from-work-order-items', {
      typing_center_id: typingCenterId || undefined,
      internal_organization_id: walkIn ? undefined : organizationId,
      walk_in: !!walkIn,
      mapped_organization_id: mappedOrg.id,
      mapped_organization_name: mappedOrg.name,
      mapped_organization_code: mappedOrg.code || '',
      work_order_line_item_ids: workOrderLineItemIds,
    });
    return res?.data?.data;
  },

  // Adds the chosen lines to a draft invoice.
  addToInvoice: async (invoiceId, workOrderLineItemIds) => {
    const res = await ApiService.post(`/api/invoices/${invoiceId}/work-order-items`, {
      work_order_line_item_ids: workOrderLineItemIds,
    });
    return res?.data?.data;
  },
};

export default billableItemsService;
